import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicPacket, DOC_TYPE_LABELS, type PublicPacketDoc, type ExpiryStatus } from "../lib/compliance";
import { Logo } from "../components/Logo";

const STATUS: Record<ExpiryStatus, { label: string; cls: string }> = {
  valid: { label: "Valid", cls: "text-verified" },
  expiring_soon: { label: "Expiring soon", cls: "text-[#c9a227]" },
  expired: { label: "Expired", cls: "text-danger" },
  no_expiry: { label: "—", cls: "text-muted" },
};

// Public carrier-packet page (the shareable /packet/<token> link). A broker sees the
// carrier's compliance-document list + expiry status — metadata only, no files.
export function CarrierPacket() {
  const { token } = useParams();
  const [data, setData] = useState<{ carrier: string; documents: PublicPacketDoc[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicPacket(token).then(setData).catch(() => setError(true)).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="grid min-h-screen place-items-center"><p className="font-mono text-sm text-muted">Loading…</p></div>;
  if (error || !data) return <div className="grid min-h-screen place-items-center px-6 text-center"><p className="text-danger">This carrier-packet link is invalid or has expired.</p></div>;

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6 px-6 py-10">
      <div className="flex items-center gap-3">
        <Logo size={36} />
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted">Carrier packet</p>
          <h1 className="font-display text-2xl font-black tracking-tight">{data.carrier}</h1>
        </div>
      </div>

      <div className="rounded-card border border-line bg-surface">
        <table className="w-full text-sm" data-testid="packet-table">
          <thead><tr className="border-b border-line text-left font-mono text-[11px] uppercase tracking-widest text-muted">
            <th className="p-3">Document</th><th className="p-3">Expires</th><th className="p-3">Status</th>
          </tr></thead>
          <tbody>
            {data.documents.length === 0 ? (
              <tr><td className="p-3 text-muted" colSpan={3}>No documents shared.</td></tr>
            ) : data.documents.map((d, i) => (
              <tr key={i} className="border-b border-line last:border-0">
                <td className="p-3 font-bold">{d.title || DOC_TYPE_LABELS[d.doc_type]}<div className="font-mono text-[10px] font-normal text-muted">{DOC_TYPE_LABELS[d.doc_type]}</div></td>
                <td className="p-3 font-mono text-muted">{d.expires_at ?? "—"}</td>
                <td className={`p-3 font-mono text-[11px] font-bold ${STATUS[d.status].cls}`}>{STATUS[d.status].label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-center font-mono text-[11px] text-muted">Shared via RecruitTrucker · document files are provided by the carrier on request</p>
    </div>
  );
}
